import {
  getChildAndYouthFieldMappings,
  getStudentTravelFieldMappings,
  FieldMapping,
} from './fieldsLegendData';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentFormType } from '@/hooks/useStudentFormSchema';

interface FieldsLegendProps {
  type: StudentFormType;
}

const columnHelper = createColumnHelper<FieldMapping>();

export const FieldsLegend = ({ type }: FieldsLegendProps) => {
  const { formatMessage } = useIntl();
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(type);

  const data = useMemo(() => {
    if (type === 'child-and-youth') {
      return getChildAndYouthFieldMappings(disableHalfProduct);
    }
    return getStudentTravelFieldMappings(disableHalfProduct);
  }, [type, disableHalfProduct]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.column, {
        id: 'column',
        header: formatMessage({ defaultMessage: 'Kolumn' }),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.type, {
        id: 'type',
        header: formatMessage({ defaultMessage: 'Typ' }),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.required, {
        id: 'required',
        header: () => formatMessage({ defaultMessage: 'Obligatorisk' }),
        cell: (info) =>
          info.getValue() ? (
            <FormattedMessage defaultMessage="Ja" />
          ) : (
            <FormattedMessage defaultMessage="Nej" />
          ),
      }),
      columnHelper.accessor((row) => row.description, {
        id: 'description',
        header: () => formatMessage({ defaultMessage: 'Beskrivning' }),
        cell: (info) => info.getValue(),
      }),
    ];
  }, [formatMessage]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className="w-full bg-background-50 text-left">
      <caption className="sr-only">
        <FormattedMessage defaultMessage="Fältbeskrivningar för importfilen" />
      </caption>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                scope="col"
                className="border border-background-300 p-2 text-text-700"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-background-100">
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className="border border-background-300 p-2 align-top"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
